import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "gs_recently_viewed";
const MAX_ITEMS = 12;

function readIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Tracks recently viewed product IDs in localStorage.
 * Returns { ids, addId } where ids are ordered most-recent first.
 * `addId` moves an existing id to the front or inserts new, capped at MAX_ITEMS.
 */
export default function useRecentlyViewed() {
  const [ids, setIds] = useState(readIds);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setIds(readIds());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addId = useCallback((id) => {
    if (!id) return;
    setIds((prev) => {
      const next = [id, ...prev.filter((existing) => existing !== id)].slice(
        0,
        MAX_ITEMS
      );
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / privacy-mode errors */
      }
      return next;
    });
  }, []);

  return { ids, addId };
}
